import { RefreshCw } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  useGetConnectionHealth,
  useRefreshConnectionHealth,
} from '@/features/integrations/api';
import { cn } from '@/lib/utils';

/**
 * "Check again" — re-probes every connection now.
 *
 * The sweep runs every six hours, which is the wrong latency for the moment
 * that matters: someone has just reconnected Instagram, or paid the Meta bill
 * that was blocking their WhatsApp number, and wants to see it clear. Without
 * this they either wait out a window with no visible end, or conclude the fix
 * did not work and open a ticket about a problem they already solved.
 *
 * It shows when the verdicts were last taken, so a stale-looking screen is
 * legible rather than mysterious. The server holds a one-minute cooldown per
 * organisation — each probe is a live Graph or Stripe call — and returns the
 * stored verdicts when it is in force, which is why this never reports failure
 * for a button that worked a moment ago.
 */
export function ConnectionHealthRefresh({
  className,
}: {
  className?: string;
}) {
  const { lastCheckedAt, isLoading } = useGetConnectionHealth();
  const { refreshConnectionHealth, isRefreshing } =
    useRefreshConnectionHealth();

  if (isLoading) return null;

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <Button
        variant="outline"
        size="sm"
        onClick={() => refreshConnectionHealth()}
        disabled={isRefreshing}
      >
        <RefreshCw
          className={cn('mr-1.5 h-3.5 w-3.5', isRefreshing && 'animate-spin')}
        />
        {isRefreshing ? 'Checking…' : 'Check again'}
      </Button>
      {lastCheckedAt ? (
        <span className="text-xs text-muted-foreground">
          Checked {relativeTime(lastCheckedAt)}
        </span>
      ) : null}
    </div>
  );
}

/** Coarse on purpose — "4 hours ago" is the useful precision here. */
function relativeTime(iso: string): string {
  const minutes = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);

  if (minutes < 2) return 'just now';
  if (minutes < 60) return `${minutes} minutes ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return hours === 1 ? '1 hour ago' : `${hours} hours ago`;

  const days = Math.floor(hours / 24);
  return days === 1 ? 'yesterday' : `${days} days ago`;
}
